import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Privacy policy for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-1313">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-1314">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1315">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-1316">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-1317">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-1318">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-1319">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1320">
            <p data-id="element-1321">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-1322">
              <li data-id="element-1323">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-1324">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-1325">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-1326">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1327">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1328">
              <ul className="list-disc pl-6 space-y-2" data-id="element-1329">
                <li data-id="element-1330">
                  <strong data-id="element-1331">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-1332">
                  <strong data-id="element-1333">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-1334">
                  <strong data-id="element-1335">Do we receive information from third parties?</strong>{' '}
                  We may receive technical and advertising data from third
                  parties, including advertising platforms and analytics
                  providers.
                </li>
                <li data-id="element-1336">
                  <strong data-id="element-1337">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-1338">
                  <strong data-id="element-1339">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-1340">
                  <strong data-id="element-1341">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-1342">
                  <strong data-id="element-1343">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-1344">
                  <strong data-id="element-1345">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 12).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-1346">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1347">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-1348">
              <li data-id="element-1349">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-1350">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-1351">SMS / TEXT MESSAGE COMMUNICATIONS</li>
              <li data-id="element-1352">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-1353">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-1354">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-1355">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-1356">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-1357">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-1358">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-1359">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-1360">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-1361">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1362">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1363">
              <h3 className="font-bold text-lg" data-id="element-1364">
                Personal information you disclose to us
              </h3>
              <p data-id="element-1365">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-1366">
                <strong data-id="element-1367">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1368">
                <li data-id="element-1369">Names</li>
                <li data-id="element-1370">Phone numbers</li>
                <li data-id="element-1371">Email addresses</li>
                <li data-id="element-1372">Mailing addresses</li>
                <li data-id="element-1373">Education level and interests</li>
                <li data-id="element-1374">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1375">
                Information automatically collected
              </h3>
              <p data-id="element-1376">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1377">
                <li data-id="element-1378">IP address</li>
                <li data-id="element-1379">Browser and device characteristics</li>
                <li data-id="element-1380">Operating system</li>
                <li data-id="element-1381">Language preferences</li>
                <li data-id="element-1382">Referring URLs</li>
                <li data-id="element-1383">Device name and type</li>
                <li data-id="element-1384">Pages viewed and time spent</li>
                <li data-id="element-1385">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-1386">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1387">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1388">
              <p data-id="element-1389">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1390">
                <li data-id="element-1391">To facilitate account creation and authentication</li>
                <li data-id="element-1392">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-1393">To respond to user inquiries and offer support</li>
                <li data-id="element-1394">To send administrative information to you</li>
                <li data-id="element-1395">To fulfill and manage your requests</li>
                <li data-id="element-1396">To enable user-to-user communications</li>
                <li data-id="element-1397">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-1398">To send you marketing and promotional communications</li>
                <li data-id="element-1399">To deliver targeted advertising to you</li>
                <li data-id="element-1400">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-1401">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-1402">To save or protect an individual's vital interest</li>
                <li data-id="element-1403">To comply with our legal obligations</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1404">Email Communications</h3>
              <p data-id="element-1405">
                When you submit a form on our website, you may consent to
                receive marketing and promotional emails from
                FindOnlineDegrees.org (operated by Media Matchers LLC) and its
                educational partners at the email address you provide. Marketing
                emails are sent from contact@findonlinedegrees.org. Every
                marketing email includes an unsubscribe link. You may opt out at
                any time and your request will be honored within 10 business
                days. Opting out of marketing email does not affect
                transactional or administrative messages related to a specific
                inquiry you have submitted.
              </p>
            </div>
          </div>

          {/* 3. SMS / Text Message Communications */}
          <div data-id="element-1406">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1407">
              3. SMS / TEXT MESSAGE COMMUNICATIONS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1408">
              <h3 className="font-bold text-lg" data-id="element-1409">How We Collect SMS Consent</h3>
              <p data-id="element-1410">
                When you submit a form on our website, you may expressly consent
                to receive autodialed and/or pre-recorded marketing text
                messages (SMS/MMS) from FindOnlineDegrees.org (operated by Media
                Matchers LLC) and its educational partners at the mobile phone
                number you provide. This consent is obtained at the point of
                form submission and is documented with the date, time, IP
                address, and page URL at which consent was given.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1411">
                How We Use Your Phone Number for SMS
              </h3>
              <p data-id="element-1412">
                If you have provided consent, we may send you text messages for
                purposes including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1413">
                <li data-id="element-1414">
                  Information about educational programs matching your interests
                </li>
                <li data-id="element-1415">Follow-up communications related to your inquiry</li>
                <li data-id="element-1416">
                  Promotional offers and marketing messages from us or our
                  educational partners
                </li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1417">
                Consent Is Not Required
              </h3>
              <p data-id="element-1418">
                Your consent to receive SMS/MMS messages is never a condition of
                using our services or receiving information. You may choose not
                to provide consent and still access our website and resources.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1419">
                Message Frequency and Rates
              </h3>
              <p data-id="element-1420">
                Message frequency varies based on your interactions and
                preferences. Standard message and data rates may apply depending
                on your carrier and plan.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1421">How to Opt Out</h3>
              <p data-id="element-1422">
                You may revoke your consent and opt out of SMS communications at
                any time by:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1423">
                <li data-id="element-1424">Replying STOP to any text message you receive from us</li>
                <li data-id="element-1425">
                  Contacting us directly using the information in Section 12
                </li>
              </ul>
              <p data-id="element-1426">
                Opt-out requests will be honored promptly. After opting out, you
                may receive a single confirmation message. You will not receive
                further SMS marketing messages unless you provide new consent.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1427">For Assistance</h3>
              <p data-id="element-1428">
                Reply HELP to any text message for support information, or
                contact us directly using the information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1429">
                Third-Party SMS Senders
              </h3>
              <p data-id="element-1430">
                When you consent to receive communications from our educational
                partners, those partners may contact you directly via SMS under
                their own messaging programs and privacy policies. We encourage
                you to review the privacy policies of any educational
                institution or partner that contacts you.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1431">Record Retention</h3>
              <p data-id="element-1432">
                We retain records of SMS consent, including timestamp, IP
                address, form URL, and submitted form data, in accordance with
                our general data retention practices described in Section 6.
              </p>
            </div>
          </div>

          {/* 4. When and With Whom Do We Share */}
          <div data-id="element-1433">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1434">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1435">
              <p data-id="element-1436">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1437">
                <li data-id="element-1438">
                  <strong data-id="element-1439">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-1440">
                  <strong data-id="element-1441">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-1442">
                  <strong data-id="element-1443">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-1444">
                  <strong data-id="element-1445">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-1446">
                  <strong data-id="element-1447">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 5. Cookies and Tracking */}
          <div data-id="element-1448">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1449">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1450">
              <p data-id="element-1451">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 6. How Long Do We Keep */}
          <div data-id="element-1452">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1453">
              6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1454">
              <p data-id="element-1455">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 7. How Do We Keep Information Safe */}
          <div data-id="element-1456">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1457">
              7. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1458">
              <p data-id="element-1459">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 8. Minors */}
          <div data-id="element-1460">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1461">
              8. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1462">
              <p data-id="element-1463">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 9. Privacy Rights */}
          <div data-id="element-1464">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1465">
              9. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1466">
              <p data-id="element-1467">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1468">
                <li data-id="element-1469">The right to access your personal information</li>
                <li data-id="element-1470">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-1471">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-1472">The right to opt out of marketing communications</li>
                <li data-id="element-1473">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-1474">
                To exercise these rights, please contact us using the
                information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1475">
                Opt Out of Marketing Email
              </h3>
              <p data-id="element-1476">
                To opt out of marketing email: Click the unsubscribe link in any
                marketing email you receive from us, or contact us at the
                address in Section 12. Requests are processed within 10 business
                days.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1477">
                California Residents — Opt Out of Sale/Sharing
              </h3>
              <p data-id="element-1478">
                California residents — opt out of sale/sharing of personal
                information: To exercise your right to opt out under CCPA/CPRA,
                contact us at the address in Section 12 with the subject line
                "California Privacy Opt-Out." We will process your request
                within 15 business days.
              </p>
            </div>
          </div>

          {/* 10. Do Not Track */}
          <div data-id="element-1479">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1480">
              10. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1481">
              <p data-id="element-1482">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 11. Updates */}
          <div data-id="element-1483">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1484">
              11. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1485">
              <p data-id="element-1486">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 12. Contact Us */}
          <div data-id="element-1487">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1488">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1489">
              <p data-id="element-1490">
                If you have questions or comments about this notice, please
                contact us at:
              </p>
              <p className="font-semibold" data-id="element-1491">Media Matchers LLC</p>
              <p data-id="element-1492">
                1865 Chestnut Street
                <br data-id="element-1493" />
                San Francisco, CA 94123
              </p>
              <p data-id="element-1494">
                Email:{' '}
                <a href="mailto:contact@findonlinedegrees.org" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-1495">
                  contact@findonlinedegrees.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}